import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { PricingHeader, PricingHeaderCreate } from '../types';
import Modal from '../components/Modal';
import PricingHeaderForm from '../components/PricingHeaderForm';
import FilterToggle from '../components/FilterToggle';

const PricingHeaders: React.FC = () => {
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHeader, setEditingHeader] = useState<PricingHeader | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'deleted'>('active');
  const pageSize = 10;

  useEffect(() => {
    // Ensure currentPage is always valid before fetching
    const validPage = Math.max(1, currentPage);
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
      return;
    }
    fetchPricingHeaders();
  }, [currentPage, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPricingHeaders = async () => {
    try {
      setLoading(true);
      // Calculate skip value for backend - ensure it's never negative
      const validPage = Math.max(1, currentPage);
      const skip = Math.max(0, (validPage - 1) * pageSize);
      const response = await apiService.getPricingHeaders(skip, pageSize);
      
      // The backend returns an array directly, not a paginated response
      if (Array.isArray(response)) {
        // Filter based on filterStatus
        let filteredHeaders = response;
        if (filterStatus === 'active') {
          filteredHeaders = response.filter(h => h.is_active);
        } else if (filterStatus === 'deleted') {
          filteredHeaders = response.filter(h => !h.is_active);
        }
        
        setPricingHeaders(filteredHeaders);
        // Since we don't have total count from backend, disable pagination for now
        // or estimate based on whether we got a full page
        setTotalPages(response.length === pageSize ? currentPage + 1 : currentPage);
      } else {
        console.error('Invalid response structure:', response);
        setPricingHeaders([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching pricing headers:', error);
      // Set default values on error
      setPricingHeaders([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingHeader(null);
    setShowModal(true);
  };

  const handleEdit = (header: PricingHeader) => {
    setEditingHeader(header);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this pricing header?')) {
      try {
        // Soft delete by setting is_active to false
        await apiService.updatePricingHeader(id, { is_active: false });
        fetchPricingHeaders();
      } catch (error) {
        console.error('Error deleting pricing header:', error);
      }
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await apiService.updatePricingHeader(id, { is_active: true });
      fetchPricingHeaders();
    } catch (error) {
      console.error('Error restoring pricing header:', error);
    }
  };

  const handleSubmit = async (data: PricingHeaderCreate) => {
    try {
      if (editingHeader) {
        await apiService.updatePricingHeader(editingHeader.pricing_header_id, data);
      } else {
        await apiService.createPricingHeader(data);
      }
      setShowModal(false);
      fetchPricingHeaders();
    } catch (error) {
      console.error('Error saving pricing header:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Pricing Headers</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create Pricing Header
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <FilterToggle
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'deleted', label: 'Deleted' }
            ]}
          />
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Category</th>
                <th>Region</th>
                <th>Standard</th>
                <th>Status</th>
                <th>Effective Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pricingHeaders && pricingHeaders.length > 0 ? pricingHeaders.map((header) => (
                <tr key={header.pricing_header_id} className={!header.is_active ? 'deleted-row' : ''}>
                  <td>{header.pricing_header_id}</td>
                  <td>{header.name}</td>
                  <td>{header.description || '-'}</td>
                  <td>{header.category || '-'}</td>
                  <td>{header.region || '-'}</td>
                  <td>
                    <span className={`badge ${header.is_standard ? 'badge-info' : 'badge-secondary'}`}>
                      {header.is_standard ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${header.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {header.is_active ? 'Active' : 'Deleted'}
                    </span>
                  </td>
                  <td>{new Date(header.effective_date).toLocaleDateString()}</td>
                  <td>
                    {header.is_active ? (
                      <>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(header)}
                          style={{ marginRight: '5px' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(header.pricing_header_id)}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleRestore(header.pricing_header_id)}
                      >
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                    No pricing headers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span>Page {Math.max(1, currentPage)} of {Math.max(1, totalPages)}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.max(1, totalPages)))}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editingHeader ? 'Edit Pricing Header' : 'Create Pricing Header'}
          onClose={() => setShowModal(false)}
        >
          <PricingHeaderForm
            initialData={editingHeader}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default PricingHeaders;